import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchResumeEvaluationAudit } from '../../services/adminAiUsageService';
import './AiUsageReport.css';

const TABS = [
  { id: 'resume', label: 'Resume evaluation usage' },
  { id: 'bq', label: 'BQ Mock usage' },
];

const BQ_MOCK_CATEGORY = 'BQ_MOCK';

const formatTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
};

const formatCost = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `$${n.toFixed(4)}`;
};

const formatTokens = (v) => (v == null ? '—' : String(v));

function UsageTable({ rows, contextHeader }) {
  if (rows.length === 0) {
    return <p className="ai-usage__muted">No usage recorded yet.</p>;
  }
  return (
    <div className="ai-usage__scroll">
      <table className="ai-usage__table">
        <thead>
          <tr>
            <th>Time</th>
            <th>User</th>
            <th>Operation</th>
            <th>{contextHeader}</th>
            <th>Tokens</th>
            <th>Cost</th>
            <th>OK</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{formatTime(row.createdAt)}</td>
              <td>{row.actorDisplay || '—'}</td>
              <td>{row.operation || '—'}</td>
              <td>{row.candidateName || '—'}</td>
              <td>{formatTokens(row.totalTokens)}</td>
              <td>{formatCost(row.estimatedCostUsd)}</td>
              <td>{row.success ? 'Yes' : 'No'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AiUsageReport() {
  const [activeTab, setActiveTab] = useState('resume');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchResumeEvaluationAudit();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load usage report.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resumeRows = useMemo(
    () => rows.filter((row) => row.evaluationCategory !== BQ_MOCK_CATEGORY),
    [rows]
  );

  const bqRows = useMemo(
    () => rows.filter((row) => row.evaluationCategory === BQ_MOCK_CATEGORY),
    [rows]
  );

  const activeRows = activeTab === 'bq' ? bqRows : resumeRows;

  return (
    <div className="ai-usage">
      <header className="ai-usage__header">
        <h1 className="ai-usage__title">AI Usage Report</h1>
        <p className="ai-usage__lead">Track resume evaluation, rewrite, and BQ mock API usage.</p>
      </header>

      <div className="ai-usage__tabs" role="tablist" aria-label="Usage report sections">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`ai-usage__tab${activeTab === tab.id ? ' ai-usage__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error ? <div className="ai-usage__error">{error}</div> : null}

      <section className="ai-usage__panel" role="tabpanel">
        {loading ? (
          <p className="ai-usage__muted">Loading…</p>
        ) : (
          <UsageTable
            rows={activeRows}
            contextHeader={activeTab === 'bq' ? 'Context' : 'Candidate'}
          />
        )}
      </section>
    </div>
  );
}
